import { useCallback, useState } from "react";
import { useMotivoDemissaoFilters } from "../hooks/MotivoDemissao_lista/filters";
import { useMotivoDemissaoPagination } from "../hooks/MotivoDemissao_lista/pagination";
import { processError } from "../../../../common/utils/base_error";
import { useCreateDebounceHandler, useDebouncedCallback } from "../../../../common/hooks/use_debounced_callback";
import { MotivoDemissao } from "../../domain/models/motivo_demissao";
import { MotivoDemissaoForm } from "../schemas/MotivoDemissao_lista/motivo_demissao";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { useChangeHistory } from "../../../../common/hooks/use_change_history";
import { LogTipoTabela } from "../../../../common/utils/log_tabela_tipo";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";

/**
 * useMotivoDemissaoListaViewModel
 */

type LoadMotivoDemissaoState = {
  tipoPesquisa?: number;
  motivoDemissao?: string;
  status?: number;
  page?: number;
  rowsPerPage?: number;
};

export const useMotivoDemissaoListaViewModel = () => {
  const {
    commons: {
      getAllLogAlteracaoUseCase,
      getAllLogAlteracaoByParentUseCase,
    },
    rh: {
      getMotivosDemissaoUseCase,
      createMotivoDemissaoUseCase,
      updateMotivoDemissaoUseCase,
      deleteMotivoDemissaoUseCase,
    },
  } = useDependencyInjection();

  const debouncedRef = useCreateDebounceHandler(750);

  const { pushErrorNotification, pushInfoNotification } = usePageLoader();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [selectedMotivoDemissao, setSelectedMotivoDemissao] = useState<MotivoDemissao>();

  const [openCadastroModal, setOpenCadastroModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);

  const [errorMessage, setErrorMessage] = useState<string>();

  const [motivosDemissao, setMotivosDemissao] = useState<MotivoDemissao[]>();

  const filters = useMotivoDemissaoFilters();
  const pagination = useMotivoDemissaoPagination();

  const changeHistory = useChangeHistory({
    getAllLogAlteracaoUseCase,
    getAllLogAlteracaoByParentUseCase,
    onSetError: error => pushErrorNotification("Erro", [error]),
  });

  /**
   * Funções de carregamento
   */

  const loadMotivoDemissao = useCallback(async (funcState?: LoadMotivoDemissaoState) => {
    try {
      const filteredTipoPesquisa = funcState?.tipoPesquisa ?? filters.tipoPesquisa;
      const filteredMotivoDemissao = funcState?.motivoDemissao ?? filters.motivoDemissao;
      const filteredStatus = funcState?.status ?? filters.status;

      const filteredPage = funcState?.page ?? pagination.page;
      const filteredRowsPerPage = funcState?.rowsPerPage ?? pagination.rowsPerPage;

      await getMotivosDemissaoUseCase.getMotivosDemissao(
        filteredTipoPesquisa,
        filteredMotivoDemissao,
        filteredStatus,
        filteredPage,
        filteredRowsPerPage,
      );

      setMotivosDemissao(getMotivosDemissaoUseCase.items);

      pagination.setRowsPerPage(getMotivosDemissaoUseCase.pageSize);
      pagination.setTotalItems(getMotivosDemissaoUseCase.totalItems);
    } catch (e: unknown) {
      setMotivosDemissao([]);
      throw e;
    }
  }, [
    filters,
    pagination,
    getMotivosDemissaoUseCase,
  ]);

  /**
   * useEffect
   */

  usePageStartBootstrap(async () => {
    try {
      setErrorMessage(undefined);
      await loadMotivoDemissao();
    } catch (e: unknown) {
      setErrorMessage(processError(e, "Falha ao buscar motivos de demissão!")[0]);
    }
  });

  /**
   * Eventos
   */

  const handleReload = useCallback(async () => {
    try {
      setLoading(true);
      await loadMotivoDemissao();
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, 'Falha ao buscar motivos de demissão!'));
    } finally {
      setLoading(false);
    }
  }, [
    loadMotivoDemissao,
    pushErrorNotification,
  ]);

  const handleSubmitMotivoDemissao = useCallback(async (form: MotivoDemissaoForm) => {
    if (!loading && !saving) {
      try {
        setSaving(true);

        if (selectedMotivoDemissao) {
          await updateMotivoDemissaoUseCase.updateMotivoDemissao(
            selectedMotivoDemissao.idMotivoDemissao,
            form.ativo,
            form.descricaoMotivo,
          );
        } else {
          await createMotivoDemissaoUseCase.createMotivoDemissao(
            form.ativo,
            form.descricaoMotivo,
          );
        }

        await loadMotivoDemissao();
        pushInfoNotification('Sucesso', 'Registro salvo com sucesso!');
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao criar motivo de demissão!"));
      } finally {
        setSaving(false);
        setOpenCadastroModal(false);
        setSelectedMotivoDemissao(undefined);
      }
    }
  }, [
    loading,
    saving,
    selectedMotivoDemissao,
    createMotivoDemissaoUseCase,
    updateMotivoDemissaoUseCase,
    loadMotivoDemissao,
    pushErrorNotification,
    pushInfoNotification,
  ]);

  const handleTipoPesquisaChange = useDebouncedCallback(debouncedRef, (tipoPesquisa: number) => {
    useMotivoDemissaoFilters.getState().setTipoPesquisa(tipoPesquisa);
    return async () => {
      try {
        setLoading(true);
        useMotivoDemissaoPagination.getState().setPage(1);
        await loadMotivoDemissao({ tipoPesquisa, page: 1 });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, 'Falha ao buscar motivos de demissão!'));
      } finally {
        setLoading(false);
      }
    };
  }, [
    pushErrorNotification,
  ]);

  const handleMotivoDemissaoChange = useDebouncedCallback(debouncedRef, (motivoDemissao: string) => {
    useMotivoDemissaoFilters.getState().setMotivoDemissao(motivoDemissao);
    return async () => {
      try {
        setLoading(true);
        useMotivoDemissaoPagination.getState().setPage(1);
        await loadMotivoDemissao({ motivoDemissao, page: 1 });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, 'Falha ao buscar motivos de demissão!'));
      } finally {
        setLoading(false);
      }
    };
  }, [
    pushErrorNotification,
  ]);

  const handleStatusChange = useDebouncedCallback(debouncedRef, (status: number) => {
    useMotivoDemissaoFilters.getState().setStatus(status);
    return async () => {
      try {
        setLoading(true);
        useMotivoDemissaoPagination.getState().setPage(1);
        await loadMotivoDemissao({ status, page: 1 });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, 'Falha ao buscar motivos de demissão!'));
      } finally {
        setLoading(false);
      }
    };
  }, [
    loadMotivoDemissao,
    pushErrorNotification,
  ]);

  const handleMotivoDemissaoCadastroOpenState = useCallback((idMotivoDemissao: number | null, openState: boolean) => {
    if (openState) {
      if (idMotivoDemissao && motivosDemissao) {
        setSelectedMotivoDemissao(motivosDemissao.find(x => x.idMotivoDemissao === idMotivoDemissao));
      } else {
        setSelectedMotivoDemissao(undefined);
      }
    }
    setOpenCadastroModal(openState);
  }, [motivosDemissao]);

  const handleMotivoDemissaoDeleteOpenState = useCallback((idMotivoDemissao: number | null, openState: boolean) => {
    if (openState) {
      if (idMotivoDemissao && motivosDemissao) {
        setSelectedMotivoDemissao(motivosDemissao.find(x => x.idMotivoDemissao === idMotivoDemissao));
      } else {
        setSelectedMotivoDemissao(undefined);
      }
    }
    setOpenDeleteModal(openState);
  }, [motivosDemissao]);

  const handlePageChange = useCallback(async (page: number) => {
    try {
      setLoading(true);
      useMotivoDemissaoPagination.getState().setPage(page + 1);
      await loadMotivoDemissao({ page: page + 1 });
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, 'Falha ao buscar motivos de demissão!'));
    } finally {
      setLoading(false);
    }
  }, [
    loadMotivoDemissao,
    pushErrorNotification,
  ]);

  const handleRowsPerPageChange = useCallback(async (rowsPerPage: number) => {
    try {
      setLoading(true);
      useMotivoDemissaoPagination.getState().setPage(1);
      useMotivoDemissaoPagination.getState().setRowsPerPage(rowsPerPage);
      await loadMotivoDemissao({ rowsPerPage, page: 1 });
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, 'Falha ao buscar motivos de demissão!'));
    } finally {
      setLoading(false);
    }
  }, [
    loadMotivoDemissao,
    pushErrorNotification,
  ]);

  const handleConfirmarDeletarMotivoDemissao = useCallback(async () => {
    if (selectedMotivoDemissao) {
      try {
        setSaving(true);
        await deleteMotivoDemissaoUseCase.deleteMotivoDemissao(selectedMotivoDemissao.idMotivoDemissao);
        await loadMotivoDemissao();
        pushInfoNotification('Sucesso', 'Registro excluido com sucesso!');
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, 'Falha ao buscar motivos de demissão!'));
      } finally {
        setSaving(false);
        setOpenDeleteModal(false);
        setSelectedMotivoDemissao(undefined);
      }
    }
  }, [
    selectedMotivoDemissao,
    deleteMotivoDemissaoUseCase,
    loadMotivoDemissao,
    pushErrorNotification,
    pushInfoNotification,
  ]);

  const handleOpenChangeHistory = useCallback(() => {
    if (selectedMotivoDemissao) {
      changeHistory.handleOpenChangeHistory(selectedMotivoDemissao.idMotivoDemissao, LogTipoTabela.MOTIVO_DEMISSAO, false);
    }
  }, [
    selectedMotivoDemissao,
    changeHistory,
  ]);

  return {
    loading,
    saving,
    selectedMotivoDemissao,
    openCadastroModal,
    openDeleteModal,
    changeHistory,
    errorMessage,
    motivosDemissao: motivosDemissao ?? [],
    filters,
    pagination,
    handleReload,
    handleSubmitMotivoDemissao,
    handleTipoPesquisaChange,
    handleMotivoDemissaoChange,
    handleStatusChange,
    handleMotivoDemissaoCadastroOpenState,
    handleMotivoDemissaoDeleteOpenState,
    handlePageChange,
    handleRowsPerPageChange,
    handleConfirmarDeletarMotivoDemissao,
    handleOpenChangeHistory,
  };
}